import { CustomMeasure } from '@counsel-project/counsel-audit-backend-api'
import CancelIcon from '@mui/icons-material/Cancel'
import DeleteIcon from '@mui/icons-material/DeleteRounded'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useMemo, useState } from 'react'
import SeverityToggle from './SeverityToggle'
import getUser from '../../util/auth/getUser'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import ListSubheader from '@mui/material/ListSubheader'
import type { SelectChangeEvent } from '@mui/material/Select'
import type { SxProps, Theme } from '@mui/material/styles'
import { DOCUMENT_TYPES } from '@counsel-project/counsel-generation-api'
import {
  ALL_DETECTED_DESCRIPTION,
  ALL_DETECTED_LABEL,
  ALL_DETECTED_VALUE,
  ANY_DESCRIPTION,
  ANY_LABEL,
  ANY_VALUE,
} from './MeasureItem.constants'

// Section-header styling for the select's group labels. Styled as an uppercase
// category label (not a muted menu row) so it doesn't read as a disabled option.
// The sticky background comes from ListSubheader's default paper color.
const PARTITION_HEADER_SX: SxProps<Theme> = {
  pointerEvents: 'none',
  lineHeight: 2,
  fontSize: '0.65rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'text.secondary',
}

function getDocumentTypeChipLabel(value: string): string {
  if (value === ALL_DETECTED_VALUE) {
    return ALL_DETECTED_LABEL
  }
  if (value === ANY_VALUE) {
    return ANY_LABEL
  }
  return value
}

function getDocumentTypeSelectValue(allDetected: boolean, cleanedTypes: string[]): string[] {
  if (allDetected) {
    return [ALL_DETECTED_VALUE]
  }
  if (cleanedTypes.length) {
    return cleanedTypes
  }
  return [ANY_VALUE]
}

export type MeasureItemProps = {
  measure: CustomMeasure
  onChange?: (measure: CustomMeasure) => void
  onDelete?: () => void
  isFirst?: boolean
}

export default function MeasureItem({
  measure,
  onChange,
  onDelete,
  isFirst,
}: Readonly<MeasureItemProps>) {
  const [open, setOpen] = useState(false)
  const user = getUser()

  const handleChangeTitle = (title: string) => {
    onChange?.({ ...measure, title })
  }

  const handleChangeDescription = (description: string) => {
    onChange?.({ ...measure, description })
  }

  const handleChangeSeverity = (severity: number) => {
    onChange?.({ ...measure, severity })
  }

  const handleChangeModel = (model: string) => {
    const numValue = Number(model)
    onChange?.({
      ...measure,
      model: !isNaN(numValue) && model !== '' ? numValue : undefined,
    })
  }

  // Clean persisted data at the boundary: dedupe (duplicates would break chip
  // keys and the all-selected detection) and strip sentinel values that must
  // never appear as real types. Memoized - this re-runs only when the types
  // change, not on every keystroke re-render.
  const { documentTypes, isAllDetected, selectValue } = useMemo(() => {
    const cleanedTypes = [...new Set(measure.documentTypes ?? [])].filter(
      (documentType) => documentType !== ALL_DETECTED_VALUE && documentType !== ANY_VALUE
    )
    // "All Detected" is stored as every concrete type, but shown as a single meta-chip.
    const allDetected =
      cleanedTypes.length >= DOCUMENT_TYPES.length &&
      DOCUMENT_TYPES.every((documentType) => cleanedTypes.includes(documentType))
    // The value the Select actually renders/highlights: collapse the full list to
    // the single "All Detected" sentinel, and show an empty list as "Any Document
    // Type" (an empty allowlist means the measure applies to everything).
    const value = getDocumentTypeSelectValue(allDetected, cleanedTypes)
    return { documentTypes: cleanedTypes, isAllDetected: allDetected, selectValue: value }
  }, [measure.documentTypes])

  const handleDocumentTypesChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event
    const nextValue = value as string[]
    // The option the user just toggled on / off relative to what was shown.
    const added = nextValue.find((option) => !selectValue.includes(option))
    const removed = selectValue.find((option) => !nextValue.includes(option))

    if (added === ANY_VALUE) {
      // "Any Document Type" -> no allowlist, applies to everything.
      onChange?.({ ...measure, documentTypes: [] })
      return
    }
    if (added === ALL_DETECTED_VALUE) {
      // "All Detected" -> persist every concrete document type.
      onChange?.({ ...measure, documentTypes: [...DOCUMENT_TYPES] })
      return
    }
    if (removed === ALL_DETECTED_VALUE) {
      // Toggling the "All Detected" option off clears the selection.
      onChange?.({ ...measure, documentTypes: [] })
      return
    }
    if (added && selectValue.includes(ALL_DETECTED_VALUE)) {
      // Clicking a specific type while "All Detected" is active unchecks just
      // that type, keeping every other document type selected.
      onChange?.({
        ...measure,
        documentTypes: DOCUMENT_TYPES.filter((documentType) => documentType !== added),
      })
      return
    }
    if (selectValue.includes(ALL_DETECTED_VALUE)) {
      // Any other event while collapsed to the single "All Detected" chip is a no-op.
      return
    }
    // Normal add/remove of concrete types; strip meta sentinels defensively.
    const newDocumentTypes = nextValue.filter(
      (option) => option !== ALL_DETECTED_VALUE && option !== ANY_VALUE
    )
    onChange?.({ ...measure, documentTypes: newDocumentTypes })
  }

  // Only individual document type chips are removable - bulk chips ("All
  // Detected" / "Any") are changed through the menu instead.
  const handleDeleteDocumentType = (documentTypeToDelete: string) => {
    const newDocumentTypes = documentTypes.filter(
      (documentType) => documentType !== documentTypeToDelete
    )
    onChange?.({ ...measure, documentTypes: newDocumentTypes })
  }

  return (
    <Accordion elevation={0} expanded={open}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon data-qa-id="measure-expand-icon" />}
        onClick={() => setOpen(!open)}
        sx={{
          borderTop: open && !isFirst ? '1px solid' : 'none',
          borderColor: 'divider',
          boxShadow: open ? '0px 2px 4px 0px rgba(0, 0, 0, 0.05)' : 'none',
        }}
      >
        <Grid container spacing={1} alignItems="center" sx={{ mr: 1 }}>
          <Grid item xs>
            {onChange ? (
              <TextField
                fullWidth
                sx={{ maxWidth: '70%' }}
                value={measure.title}
                onChange={(e) => handleChangeTitle(e.target.value)}
                placeholder="Measurement Title"
                size="small"
                onClick={(e) => e.stopPropagation()}
                inputProps={{ 'data-qa-id': 'measure-title-input' }}
              />
            ) : (
              <Typography variant="body1" fontWeight="medium">
                {measure.title}
              </Typography>
            )}
          </Grid>
          <Grid item>
            <SeverityToggle
              severity={measure.severity}
              onChange={handleChangeSeverity}
              disabled={!onChange}
            />
          </Grid>
          {!!onDelete && (
            <Grid item>
              <IconButton
                onClick={onDelete}
                size="small"
                color="error"
                data-qa-id="measure-delete-button"
              >
                <DeleteIcon />
              </IconButton>
            </Grid>
          )}
        </Grid>
      </AccordionSummary>
      <AccordionDetails sx={{ backgroundColor: 'background.default' }}>
        <Box sx={{ backgroundColor: 'background.paper', p: 2, borderRadius: 1, mt: 1 }}>
          <Grid container spacing={1}>
            {!!onChange && (
              <Grid item xs>
                <Typography variant="body1" fontWeight={500}>
                  AI Instructions
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Describe to the AI what to check for in this measure
                </Typography>
              </Grid>
            )}
            {user?.admin &&
              (onChange ? (
                <Grid item>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item>
                      <Typography variant="body1" fontWeight={500}>
                        AI Model ID
                      </Typography>
                    </Grid>
                    <Grid item>
                      <TextField
                        type="number"
                        value={measure.model ?? ''}
                        onChange={(e) => handleChangeModel(e.target.value)}
                        size="small"
                        inputProps={{ min: 0 }}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Grid>
              ) : null)}
            <Grid item xs={12}>
              {onChange ? (
                <TextField
                  fullWidth
                  value={measure.description}
                  onChange={(e) => handleChangeDescription(e.target.value)}
                  placeholder="Description of what to check for in this measure"
                  multiline
                  minRows={3}
                  inputProps={{ 'data-qa-id': 'measure-instructions-input' }}
                />
              ) : (
                <Typography variant="body1" color="text.secondary">
                  {measure.description || 'No description provided'}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="body1" fontWeight={500}>
                Document Type Allowlist
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                This measure will only apply to documents chosen here.
              </Typography>
              {onChange ? (
                <FormControl fullWidth>
                  <Select
                    multiple
                    value={selectValue}
                    onChange={handleDocumentTypesChange}
                    placeholder="Select Document Types"
                    displayEmpty
                    size="small"
                    data-qa-id="measure-doctype-select"
                    MenuProps={{
                      anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                      transformOrigin: { vertical: 'top', horizontal: 'left' },
                      // maxWidth keeps the long bulk-option descriptions from
                      // stretching the menu - they wrap instead
                      PaperProps: { sx: { maxHeight: 320, maxWidth: 620 } },
                    }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip
                            key={value}
                            label={getDocumentTypeChipLabel(value)}
                            size="small"
                            // Bulk chips are not removable, only individual types
                            onDelete={
                              value === ANY_VALUE || value === ALL_DETECTED_VALUE
                                ? undefined
                                : () => handleDeleteDocumentType(value)
                            }
                            deleteIcon={
                              <CancelIcon onMouseDown={(event) => event.stopPropagation()} />
                            }
                          />
                        ))}
                      </Box>
                    )}
                  >
                    <ListSubheader sx={PARTITION_HEADER_SX}>Bulk Selection</ListSubheader>
                    <MenuItem value={ALL_DETECTED_VALUE} sx={{ whiteSpace: 'normal', py: 1 }}>
                      <span>
                        {ALL_DETECTED_LABEL}
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 0.5 }}
                        >
                          — {ALL_DETECTED_DESCRIPTION}
                        </Typography>
                      </span>
                    </MenuItem>
                    <MenuItem value={ANY_VALUE} sx={{ whiteSpace: 'normal', py: 1 }}>
                      <span>
                        {ANY_LABEL}
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 0.5 }}
                        >
                          — {ANY_DESCRIPTION}
                        </Typography>
                      </span>
                    </MenuItem>
                    <ListSubheader sx={PARTITION_HEADER_SX}>Individual Selection</ListSubheader>
                    {DOCUMENT_TYPES.map((documentType, index) => (
                      <MenuItem
                        key={`${documentType}-${index}`}
                        value={documentType}
                        sx={{ py: 0.25 }}
                      >
                        {/* All Detected shows every type checked, so unchecking one
                            reads as subtracting it from the full selection */}
                        <Checkbox
                          size="small"
                          checked={isAllDetected || documentTypes.includes(documentType)}
                          sx={{ py: 0.5, mr: 1 }}
                        />
                        {documentType}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  {isAllDetected
                    ? ALL_DETECTED_LABEL
                    : documentTypes.length
                      ? documentTypes.join(', ')
                      : ANY_LABEL}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}
